import React, { useEffect, useState } from "react";
import { Loader2, MessageSquareWarning } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const VERDICT_TONE = { correct: "text-emerald-300", wrong: "text-rose-300", dismissed: "text-muted-foreground" };

function Filter({ label, value, options, onChange, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <span className="text-xs text-muted-foreground mr-1">{label}</span>
      {options.map(([v, l]) => (
        <Button key={v || "all"} size="sm" variant={value === v ? "default" : "outline"} onClick={() => onChange(v)} data-testid={`${testId}-${v || "all"}`}>{l}</Button>
      ))}
    </div>
  );
}

/** Admin: every tester verdict on LLM callouts, with the most-rejected callouts first. */
export function FeedbackOverview() {
  const [verdict, setVerdict] = useState("");
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.feedbackAll({ verdict, kind }).then(setData).catch((e) => console.warn("feedbackAll failed", e));
  }, [verdict, kind]);

  return (
    <div className="grid gap-4" data-testid="feedback-overview">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquareWarning className="w-5 h-5 text-amber-400" />
            <h3 className="font-semibold">Callout feedback</h3>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="feedback-overview-total">{data ? `${data.total} verdicts` : ""}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <Filter label="Verdict" value={verdict} onChange={setVerdict} testId="feedback-filter-verdict" options={[["", "All"], ["dismissed", "Dismissed"], ["wrong", "Wrong"], ["correct", "Correct"]]} />
            <Filter label="Callout" value={kind} onChange={setKind} testId="feedback-filter-kind" options={[["", "All"], ["conflict", "Conflicts"], ["similar", "Similar rules"], ["interaction", "Interactions"]]} />
          </div>
        </CardContent>
      </Card>
      {!data ? (
        <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</p>
      ) : (
        <>
          <Card>
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Most-rejected callouts</p>
              {data.by_target.length === 0 && <p className="text-sm italic text-muted-foreground">No feedback yet.</p>}
              <div className="grid gap-1">
                {data.by_target.slice(0, 15).map((t, i) => (
                  <div key={t.target_key} className="flex items-center gap-2 text-sm" data-testid={`feedback-tally-${i}`}>
                    <Badge variant="outline" className="text-[10px]">{t.kind}</Badge>
                    <span className="flex-1 truncate">{t.target_label || t.target_key}</span>
                    <span className="text-rose-300 text-xs">{t.wrong} wrong</span>
                    <span className="text-muted-foreground text-xs">{t.dismissed} dismissed</span>
                    <span className="text-emerald-300 text-xs">{t.correct} correct</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="grid gap-2">
            {data.items.map((r) => (
              <Card key={r.id} data-testid={`feedback-row-${r.id}`}>
                <CardContent className="p-3 space-y-1 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-medium capitalize ${VERDICT_TONE[r.verdict]}`}>{r.verdict}</span>
                    <Badge variant="outline" className="text-[10px]">{r.kind}</Badge>
                    <span className="font-medium">{r.target_label}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{new Date(r.updated_at).toLocaleString()}</span>
                  </div>
                  {r.reason && <p className="italic">“{r.reason}”</p>}
                  <p className="text-xs text-muted-foreground">Rule: “{(r.proposed_text || "").slice(0, 200)}”</p>
                  {r.callout && <p className="text-xs text-muted-foreground">LLM said: {r.callout.slice(0, 300)}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
